import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import type { GradeBadgeCategory } from "@/components/grade-badge";
import type { ResolvedGradingCopy } from "@shared/grading";

type CategoryMeta = {
  id: string;
  slug: string;
  name: string;
  iconUrl: string | null;
  saleType: "products" | "pallets" | null;
  referencePriceLabel: string | null;
  hidePalletPackagingExample: boolean | null;
  showSpecifications: boolean | null;
  gradingTooltip: string | null;
  gradeDetailLines: Record<string, string> | null;
  inventoryPillLabel: string | null;
  /** Server-resolved grading copy (Task #156). Always present. */
  gradingResolved?: ResolvedGradingCopy;
};

function useCategories() {
  return useQuery<CategoryMeta[]>({ queryKey: ["/api/categories"] });
}

export function useReferencePriceLabel(categoryId?: string | null): string {
  const { data } = useCategories();
  if (!categoryId || !data) return "MSRP";
  const c = data.find((x) => x.id === categoryId);
  const label = (c?.referencePriceLabel || "").trim();
  return label || "MSRP";
}

export function useHidePalletPackaging(categoryId?: string | null): boolean {
  const { data } = useCategories();
  if (!categoryId || !data) return false;
  const c = data.find((x) => x.id === categoryId);
  return !!c?.hidePalletPackagingExample;
}

/**
 * Per-category toggle that controls whether the storefront product detail
 * page renders the rich Specifications block. Defaults to TRUE (show) so
 * categories that haven't been explicitly opted out keep the original
 * behavior. When the categories list hasn't loaded yet we also default to
 * true to avoid a brief flash of hidden specs.
 */
export function useShowSpecifications(categoryId?: string | null): boolean {
  const { data } = useCategories();
  if (!categoryId || !data) return true;
  const c = data.find((x) => x.id === categoryId);
  if (!c) return true;
  return c.showSpecifications !== false;
}

/**
 * Per-category grading copy + pallet-pill metadata for `<GradeBadge>`
 * (Task #156). Returns the subset of category fields the badge needs, or
 * `null` while the categories list is still loading / when the id is
 * unknown so the badge falls back to refrigerator defaults.
 */
export function useGradeBadgeCategory(
  categoryId?: string | null,
): GradeBadgeCategory | null {
  const { data } = useCategories();
  if (!categoryId || !data) return null;
  const c = data.find((x) => x.id === categoryId);
  if (!c) return null;
  return {
    name: c.name,
    iconUrl: c.iconUrl,
    saleType: c.saleType,
    gradingResolved: c.gradingResolved,
  };
}

/**
 * Hook variant for callers that need to look up the grade-badge category
 * for many different ids in the same render (cart, checkout). Returns a
 * stable function so call sites can `lookup(modelGroup.categoryId)` per
 * row without violating the rules-of-hooks.
 */
export function useGradeBadgeCategoryLookup(): (
  categoryId?: string | null,
) => GradeBadgeCategory | null {
  const { data } = useCategories();
  return useCallback(
    (categoryId?: string | null) => {
      if (!categoryId || !data) return null;
      const c = data.find((x) => x.id === categoryId);
      if (!c) return null;
      return {
        name: c.name,
        iconUrl: c.iconUrl,
        saleType: c.saleType,
        gradingResolved: c.gradingResolved,
      };
    },
    [data],
  );
}
